/* eslint-disable react-hooks/exhaustive-deps */
import { keyframes } from "@emotion/react";
import {
  Banner,
  Box,
  Breadcrumb,
  Button,
  Chip,
  Grid,
  GridColumn,
  Icon,
  Link,
  Logo,
  Text,
  ThemeProvider
} from "@modernatx/ui-kit-react";
import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Margin, usePDF } from "react-to-pdf";

import { ErrorModal } from "@/components/ErrorModal/ErrorModal";
import { InvoicePriceSummary } from "@/components/InvoicePay/InvoicePriceSummary";
import ItemizedInvoiceItemsTable from "@/components/InvoicePay/ItemizedInvoiceItemsTable";
import NoteCard from "@/components/InvoicePay/NoteCard";
import PaymentDetails from "@/components/InvoicePay/PaymentDetails";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { SchedulePayModal } from "@/components/Payments/AutoPayModal/SchedulePayModal";
import { useFeatureFlag } from "@/context/FeatureFlagContext";
import { useUserInfoContext } from "@/contexts";
import { useInvoiceDetail } from "@/hooks/useInvoiceDetail";
import { usePaymentDetails } from "@/hooks/usePaymentDetails";
import { useSchedulePayment } from "@/hooks/useSchedulePayment";
import { InvoiceDataType, ScheduledPaymentDetails } from "@/types";
import { Invoice, InvoiceStatus, PaymentInfo } from "@/types/Invoice";
import {
  formatAddress,
  formatTimestampDate,
  getPaymentStatus
} from "@/utils/InvoicePay";

import { formatInvoiceStatus } from "../utils/InvoicePay";

const fadeInOut = keyframes`
  0% { opacity: 0; transform: translate(-50%, -60%); }
  10% { opacity: 1; transform: translate(-50%, -50%); }
  90% { opacity: 1; transform: translate(-50%, -50%); }
  100% { opacity: 0; transform: translate(-50%, -40%); }
`;

const InvoiceDetailPage = () => {
  const { invoiceNo } = useParams<{ invoiceNo: string }>();
  const {
    fetchInvoice,
    isLoading: isInvoiceLoading,
    error: invoiceDetailError
  } = useInvoiceDetail();

  const {
    getScheduledPayment,
    isFetchingSchedule,
    fetchScheduleError,
    setFetchScheduleError
  } = useSchedulePayment();

  const autoPayAndSchedulePayEnabled = useFeatureFlag(
    "IS_AUTO_PAY_SCHEDULE_PAY_ENABLED"
  );

  const [invoice, setInvoice] = React.useState<Invoice | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(!!invoiceDetailError);
  const history = useHistory();

  const pages = [
    { pageId: "your-account", title: "Your Account" },
    {
      pageId: "your-account/invoices-and-payments",
      title: "Invoices and payments"
    },
    {
      pageId: `your-account/invoices-and-payments/invoices/${invoiceNo}`,
      title: `Invoice #${invoiceNo}`
    }
  ];

  React.useEffect(() => {
    const fetchInvoiceDetail = async () => {
      const invoiceDetail = await fetchInvoice(invoiceNo);
      if (invoiceDetail) {
        setInvoice(invoiceDetail);
      }
    };
    fetchInvoiceDetail();
  }, [fetchInvoice, invoiceNo]);

  const handleClickPage = React.useCallback(
    (pageId: string) => {
      history.push(`/${pageId}`);
    },
    [history]
  );

  const shouldFetchPayment =
    invoice?.status === InvoiceStatus.Paid ||
    invoice?.status === InvoiceStatus.Pending;

  const { paymentInfo, isLoading: isPaymentLoading } = usePaymentDetails({
    invoiceNo,
    shouldFetch: shouldFetchPayment
  });
  const [paymentSchedule, setPaymentSchedule] =
    React.useState<ScheduledPaymentDetails | null>(null);

  const fetchPaymentScheduleDetails = async () => {
    const scheduleDetails = await getScheduledPayment(invoiceNo);
    if (scheduleDetails) {
      setPaymentSchedule(scheduleDetails);
    }
  };

  React.useEffect(() => {
    if (!invoice || shouldFetchPayment || !autoPayAndSchedulePayEnabled) return;

    fetchPaymentScheduleDetails();
  }, [invoice, invoiceNo, shouldFetchPayment, autoPayAndSchedulePayEnabled]);

  const [schedulePayChipMessage, setSchedulePayChipMessage] = useState<
    string | null
  >(null);

  const handleScheduleSuccess = (
    newSchedule: ScheduledPaymentDetails,
    message = "Payment scheduled!"
  ) => {
    setPaymentSchedule(newSchedule);
    fetchPaymentScheduleDetails();
    setSchedulePayChipMessage(message);
    setTimeout(() => setSchedulePayChipMessage(null), 3000);
  };
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  React.useEffect(() => {
    if (invoiceDetailError && !isInvoiceLoading) {
      setErrorMessage(invoiceDetailError);
      setShowErrorModal(true);
    }
  }, [
    invoiceDetailError,
    isInvoiceLoading,
    fetchScheduleError,
    isFetchingSchedule
  ]);

  return (
    <ThemeProvider>
      <ErrorModal
        isVisible={showErrorModal}
        setVisible={setShowErrorModal}
        title={errorMessage}
        onDismiss={() => history.push(`/your-account/invoices-and-payments`)}
      />
      <Box
        sx={{
          backgroundColor: "surface-neutral-secondary",
          pt: "spacing-9",
          pb: "spacing-12",
          userSelect: "text"
        }}
      >
        <Grid>
          <GridColumn sx={{ pb: "spacing-9" }}>
            <Breadcrumb pages={pages} onClickPage={handleClickPage} />
          </GridColumn>
        </Grid>

        {isInvoiceLoading || isPaymentLoading || isFetchingSchedule ? (
          <LoadingSpinner size="xl" />
        ) : !invoice ? (
          <Text sx={{ pt: "spacing-6" }}>Invoice not found.</Text>
        ) : (
          <>
            {fetchScheduleError && (
              <Grid>
                <GridColumn
                  size={12}
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      px: "spacing-2",
                      mb: "spacing-6"
                    }}
                  >
                    <Banner
                      variant="critical"
                      title="Unable to retrieve scheduled payment"
                      text={<Text>{fetchScheduleError}</Text>}
                      onDismiss={() => setFetchScheduleError(null)}
                    />
                  </Box>
                </GridColumn>
              </Grid>
            )}
            <InvoiceDetailContent
              invoice={invoice}
              paymentInfo={paymentInfo}
              schedulePayChipMessage={schedulePayChipMessage}
              onScheduleSuccess={handleScheduleSuccess}
              paymentSchedule={paymentSchedule}
              autoPayAndSchedulePayEnabled={autoPayAndSchedulePayEnabled}
            />
          </>
        )}
      </Box>
    </ThemeProvider>
  );
};

const InvoiceDetailContent: React.FC<{
  invoice: Invoice;
  paymentInfo: PaymentInfo | null;
  paymentSchedule: ScheduledPaymentDetails | null;
  schedulePayChipMessage: string | null;
  onScheduleSuccess?: (schedule: ScheduledPaymentDetails) => void;
  autoPayAndSchedulePayEnabled: boolean;
}> = ({
  invoice,
  paymentInfo,
  paymentSchedule,
  schedulePayChipMessage,
  onScheduleSuccess,
  autoPayAndSchedulePayEnabled
}) => {
  const history = useHistory();
  const [showScheduleModal, setShowScheduleModal] = React.useState(false);
  const { userInfo } = useUserInfoContext() as {
    userInfo: { userId: string };
  };

  const hasScheduledPayment = !!paymentSchedule?.scheduledPaymentDate;

  const { toPDF, targetRef } = usePDF({
    filename: `Moderna_Invoice_${invoice.invoiceNo}.pdf`,
    page: {
      margin: Margin.SMALL,
      format: "A4"
    }
  });

  if (!invoice) return null;

  const showPaymentDetails =
    invoice.status === InvoiceStatus.Paid ||
    invoice.status === InvoiceStatus.Pending;

  const showPayNow = !showPaymentDetails;
  const showSchedulePayment =
    autoPayAndSchedulePayEnabled && !showPaymentDetails && !hasScheduledPayment;

  const showManagePayment = autoPayAndSchedulePayEnabled && hasScheduledPayment;

  const promptPayDiscount = invoice.discounts.find(
    (discount) => discount.discountType === "PROMPT_PAY"
  );

  const invoiceData: InvoiceDataType = {
    invoiceId: invoice?.invoiceNo,
    dueDate: invoice?.dueDate,
    soldToOrgId: invoice?.soldTo?.id,
    status: invoice?.status
  };

  return (
    <Grid>
      {autoPayAndSchedulePayEnabled && showScheduleModal && (
        <SchedulePayModal
          isVisible={showScheduleModal}
          setVisible={setShowScheduleModal}
          invoiceData={invoiceData}
          onScheduleSuccess={onScheduleSuccess}
          userId={userInfo?.userId}
          paymentSchedule={paymentSchedule}
        />
      )}
      {schedulePayChipMessage ? (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1,
            animation: `${fadeInOut} 3s ease-in-out forwards`
          }}
        >
          <Chip
            label={schedulePayChipMessage}
            icon="check-circle"
            variant="success"
            size="large"
          />
        </Box>
      ) : null}
      <GridColumn>
        {getPaymentStatus(formatInvoiceStatus(invoice.status))}
      </GridColumn>
      <GridColumn>
        <InvoiceHeader onClickDownload={toPDF} />
      </GridColumn>
      <GridColumn>
        <Box
          ref={targetRef}
          sx={{
            backgroundColor: "white",
            padding: "spacing-8",
            display: "flex",
            flexDirection: "column",
            borderRadius: "radius-2"
          }}
        >
          <Logo logo="moderna-logo" sx={{ height: 16, width: 48 }} />
          {/* Order numbers */}
          <Grid>
            <GridColumn>
              {
                <Text weight="bold">
                  P.O. number:
                  <Text weight="regular" sx={{ pl: "spacing-2" }}>
                    {invoice.poNumber || "--"}
                  </Text>
                </Text>
              }
              <Text weight="bold">
                Order number:
                {invoice.orderNumber ? (
                  <Text
                    sx={{ pl: "spacing-2", fontWeight: "font-weight-regular" }}
                  >
                    {invoice.orderNumber}
                  </Text>
                ) : (
                  <Text weight="regular" sx={{ pl: "spacing-2" }}>
                    Order number not available.
                  </Text>
                )}
              </Text>
              <Text weight="bold">
                Invoice number:
                {invoice.invoiceNo ? (
                  <Text
                    sx={{ pl: "spacing-2", fontWeight: "font-weight-regular" }}
                  >
                    {invoice.invoiceNo}
                  </Text>
                ) : (
                  <Text weight="regular" sx={{ pl: "spacing-2" }}>
                    Invoice number not available.
                  </Text>
                )}
              </Text>
              <Text weight="bold">
                Invoice date:
                <Text weight="regular" sx={{ pl: "spacing-2" }}>
                  {formatTimestampDate(invoice.invoiceDate)}
                </Text>
              </Text>
              <Text weight="bold">
                Payment terms:
                <Text weight="regular" sx={{ pl: "spacing-2" }}>
                  {invoice.paymentTermsDescription}
                </Text>
              </Text>
            </GridColumn>
          </Grid>

          {/* Shipping and billing addresses */}
          <Grid sx={{ px: "spacing-4", py: "spacing-10" }}>
            <GridColumn size={2} fillGapEnd={true}>
              <Text weight="bold">Ship to</Text>
              <Text>{invoice.shipTo.name}</Text>
              <Text>{formatAddress(invoice.shipTo)}</Text>
              {/* 
              todo: MD-350 to add medical license
              <Text weight="bold">
                Medical license:{" "}
                <Text weight="regular">{invoice.shipTo.medicalLicense}</Text>
              </Text> */}
              <Text weight="bold">
                Account number:{" "}
                <Text weight="regular">{invoice.shipTo.id}</Text>
              </Text>
            </GridColumn>
            <GridColumn size={2} fillGapEnd={true}>
              <Text weight="bold">Billed to</Text>
              <Text>{invoice.billTo.name}</Text>
              <Text>{formatAddress(invoice.billTo)}</Text>
              <Text weight="bold">
                Account number:{" "}
                <Text weight="regular">{invoice.billTo.id}</Text>
              </Text>
            </GridColumn>
          </Grid>

          {invoice?.orderLineItems?.length > 0 ? (
            <ItemizedInvoiceItemsTable items={invoice.orderLineItems} />
          ) : invoice?.notes?.length > 0 ? (
            <NoteCard notes={invoice.notes} />
          ) : (
            <Text>
              This order was placed outside of Moderna Direct, so detailed
              information is not available.
            </Text>
          )}
          <InvoicePriceSummary
            invoice={invoice}
            promptPayDiscount={promptPayDiscount}
          />
          {showPaymentDetails && (
            <Grid sx={{ pt: "spacing-6" }}>
              <GridColumn size={4} push={[0, 4, 8]}>
                <PaymentDetails
                  payment={paymentInfo}
                  paymentStatus={invoice.status}
                />
              </GridColumn>
            </Grid>
          )}
        </Box>
      </GridColumn>

      {showPayNow && (
        <GridColumn
          size={[4, 4, 3]}
          push={[0, 4, 9]}
          sx={{ pt: "spacing-9", pb: "spacing-2" }}
        >
          <Button
            icon="arrow-right"
            iconPosition="end"
            onClick={() => {
              history.push(`/pay?invoice=${invoice.invoiceNo}`);
            }}
          >
            Pay now
          </Button>
        </GridColumn>
      )}
      {showSchedulePayment && (
        <GridColumn size={[4, 4, 3]} push={[0, 4, 9]}>
          <Button
            variant="subtle"
            onClick={() => {
              setShowScheduleModal(true);
            }}
          >
            Schedule a payment
          </Button>
        </GridColumn>
      )}
      {showManagePayment && (
        <GridColumn size={[4, 4, 3]} push={[0, 4, 9]} sx={{ mt: "spacing-3" }}>
          <Box sx={{ display: "flex", gap: "spacing-2" }}>
            <Icon icon="arrow-counter-clockwise" size="xs" />
            <Text>
              <Text sx={{ pr: "spacing-2" }}>This invoice will be paid on</Text>
              {formatTimestampDate(paymentSchedule?.scheduledPaymentDate)}.
              <Link
                sx={{ pl: "spacing-2" }}
                onClick={() => {
                  setShowScheduleModal(true);
                }}
              >
                Manage scheduled payment
              </Link>
            </Text>
          </Box>
        </GridColumn>
      )}
    </Grid>
  );
};

const InvoiceHeader: React.FC<{
  onClickDownload: () => void;
}> = ({ onClickDownload }) => {
  return (
    <Grid sx={{ pt: "spacing-2", pb: "spacing-6" }}>
      <GridColumn size={[2, 4, 6]} sx={{ alignItems: "start" }}>
        <Text variant="h4">Invoice Details</Text>
      </GridColumn>
      <GridColumn size={[2, 4, 6]} sx={{ alignItems: "end" }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outline"
            icon="download-simple"
            iconPosition="end"
            onClick={onClickDownload}
          >
            Download invoice
          </Button>
          {/* <Button
            variant="outline"
            icon="share"
            iconPosition="end"
            onClick={() => {}}
            sx={{ ml: "spacing-3" }}
          >
            Share
          </Button> */}
        </Box>
      </GridColumn>
    </Grid>
  );
};

export default InvoiceDetailPage;
